import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import WishlistButton from './WishlistButton';
import StarRating from './StarRating';

// Cache for game details
const gameDetailsCache = new Map();

const HoverCard = ({ gameId }) => {
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFullStoryline, setShowFullStoryline] = useState(false);
  const fallbackImage = "/StockGameCover.jpg";

  // Function to navigate to game details page
  const handleViewDetails = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    navigate(`/game-details/${gameId}`);
  };

  // Function to truncate text to a certain number of words
  const truncateText = (text, wordLimit) => {
    if (!text) return 'No summary available';
    const words = text.split(' ');
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  // Function to convert YouTube URL to embed URL
  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    // Handle different YouTube URL formats
    const patterns = {
      // Regular YouTube URL
      regular: /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      // YouTube embed URL
      embed: /youtube\.com\/embed\/([^&\n?#]+)/,
      // YouTube short URL
      short: /youtu\.be\/([^&\n?#]+)/
    };

    // Check if it's already an embed URL
    if (url.includes('youtube.com/embed/')) {
      return url;
    }

    // Try to extract video ID from different URL formats
    for (const pattern of Object.values(patterns)) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
    }

    return null;
  };

  const fetchGameDetails = useCallback(async () => {
    // Check cache first
    if (gameDetailsCache.has(gameId)) {
      setGame(gameDetailsCache.get(gameId));
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/query', {
        mainTable: 'games g',
        fields: [
          'g.id',
          'g.name',
          'g.first_release_date',
          'g.coverUrl',
          'g.trailerUrl',
          'g.storyline',
          'GROUP_CONCAT(DISTINCT gen.name) as genres',
          'GROUP_CONCAT(DISTINCT t.name) as themes',
          'GROUP_CONCAT(DISTINCT ar.rating) as age_ratings',
          'GROUP_CONCAT(DISTINCT ls.language) as language_supports',
          's.steam_url'
        ],
        joins: [
          {
            type: 'LEFT',
            table: 'game_genres gg',
            on: 'g.id = gg.game_id'
          },
          {
            type: 'LEFT',
            table: 'genres gen',
            on: 'gg.genre_id = gen.id'
          },
          {
            type: 'LEFT',
            table: 'game_themes gt',
            on: 'g.id = gt.game_id'
          },
          {
            type: 'LEFT',
            table: 'themes t',
            on: 'gt.theme_id = t.id'
          },
          {
            type: 'LEFT',
            table: 'game_age_ratings gar',
            on: 'g.id = gar.game_id'
          },
          {
            type: 'LEFT',
            table: 'age_ratings ar',
            on: 'gar.age_rating_id = ar.id'
          },
          {
            type: 'LEFT',
            table: 'game_language_supports gls',
            on: 'g.id = gls.game_id'
          },
          {
            type: 'LEFT',
            table: 'language_supports ls',
            on: 'gls.language_support_id = ls.id'
          },
          {
            type: 'LEFT',
            table: 'steam_url_id s',
            on: 'g.id = s.game_id'
          }
        ],
        conditions: ['g.id = ?'],
        groupBy: ['g.id'],
        params: [gameId]
      });
      
      if (response.data && response.data.length > 0) {
        const gameData = response.data[0];
        const processedGame = {
          id: gameData.id,
          name: gameData.name,
          releaseDate: new Date(gameData.first_release_date * 1000).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: 'UTC'
          }),
          releaseDateUnix: gameData.first_release_date,
          coverUrl: gameData.coverUrl || fallbackImage,
          trailerUrl: getEmbedUrl(gameData.trailerUrl),
          storyline: gameData.storyline || 'No summary available',
          genres: gameData.genres ? gameData.genres.split(',') : [],
          themes: gameData.themes ? gameData.themes.split(',') : [],
          ageRatings: gameData.age_ratings ? gameData.age_ratings.split(',') : [],
          languageSupports: gameData.language_supports ? gameData.language_supports.split(',') : [],
          steamUrl: gameData.steam_url || null
        };

        // Cache the processed game data
        gameDetailsCache.set(gameId, processedGame);
        setGame(processedGame);
      }
    } catch (error) {
      console.error('Error fetching game details:', error);
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    let timeoutId;
    
    if (isHovered && !game && !loading) {
      // Preload data when hover starts
      timeoutId = setTimeout(() => {
        fetchGameDetails();
      }, 100); // Reduced delay for better responsiveness
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isHovered, game, loading, fetchGameDetails]);

  // Preload cover image
  useEffect(() => {
    if (game?.coverUrl) {
      const img = new Image();
      img.src = game.coverUrl;
    }
  }, [game?.coverUrl]);

  return (
    <div 
      className="absolute top-1/2 left-0 transform -translate-y-1/2 z-30 w-full max-w-[350px] bg-white p-6 rounded-xl shadow-2xl scale-0 group-hover:scale-100 transition-transform duration-300 origin-left text-gray-900"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setShowFullStoryline(false);
      }}
    >
      {loading ? (
        <div className="w-full h-56 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {isHovered && game?.trailerUrl && game.trailerUrl !== '' ? (
            <iframe
              className="w-full h-56 mb-4 rounded"
              src={game.trailerUrl}
              title={`${game.name} Trailer`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
              aria-label={`${game.name} Trailer`}
            />
          ) : (
            <img
              src={game?.coverUrl || fallbackImage}
              alt={game?.name || 'Game Cover'}
              className="w-full h-56 object-cover rounded mb-4"
              loading="lazy"
              onError={(e) => { e.target.src = fallbackImage; }}
            />
          )}

          <h3 className="font-bold text-xl mb-1 text-center">{game?.name || 'Loading...'}</h3>
          <p className="text-sm text-gray-600 mb-2 text-center">
            Releases on {game?.releaseDate || 'TBA'}
          </p>
          <div className="text-sm text-gray-700 mb-4">
            <p>{showFullStoryline ? game?.storyline : truncateText(game?.storyline, 30)}</p>
            {game?.storyline && game.storyline.split(' ').length > 30 && (
              <button
                onClick={() => setShowFullStoryline(!showFullStoryline)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-1"
              >
                {showFullStoryline ? 'Show less' : 'Show more...'}
              </button>
            )}
          </div>

          <div className="flex items-center justify-center gap-4 mb-2">
            <WishlistButton
              gameId={gameId}
              releaseDate={game?.releaseDateUnix}
            />
            <button
              onClick={handleViewDetails}
              className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View Details
            </button>
          </div>
          
          {game?.steamUrl && (
            <div className="text-center">
              <a 
                href={game.steamUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-4 py-2 bg-[#1b2838] hover:bg-[#2a3f5a] text-white text-sm font-medium rounded-md"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 0C3.582 0 0 3.582 0 8c0 3.727 2.551 6.85 6 7.744V14h-.5A1.5 1.5 0 004 15.5v.5h8v-.5a1.5 1.5 0 00-1.5-1.5H10v-2.256C13.449 14.85 16 11.727 16 8c0-4.418-3.582-8-8-8zm5.845 11.235l-2.194.893c-.148.574-.625.996-1.226.996a1.32 1.32 0 01-1.323-1.317c0-.043.001-.087.006-.13l-1.422-.58c.021-.084.031-.17.031-.259 0-.75-.6-1.354-1.343-1.354-.653 0-1.195.462-1.324 1.073l-1.66-.675c.061-.988.74-1.82 1.64-1.966l2.02-.826c.23-.636.853-1.09 1.58-1.09.737 0 1.369.472 1.586 1.122l2.198.898c.03.93.052.188.052.288 0 .895-.711 1.621-1.619 1.621-.473 0-.896-.207-1.186-.535l-1.564.638c.033.142.052.289.052.441 0 .973-.77 1.769-1.748 1.769-.878 0-1.584-.635-1.735-1.472l-.851-.349c.274-.695.953-1.189 1.752-1.189.31 0 .6.076.855.209l1.797.733c.132-.413.516-.713.974-.713.566 0 1.024.458 1.024 1.024 0 .153-.033.299-.094.428l1.77.728c.009-.134.036-.263.036-.401 0-.753-.608-1.343-1.363-1.343-.16 0-.314.03-.457.082l-1.731-.708c-.111.488-.39.901-.78 1.158z"/>
                </svg>
                View on Steam
              </a>
            </div>
          )}
          
          {/* Star Rating */}
          <div className="flex justify-center mt-3">
            <StarRating 
              gameId={gameId} 
              size="lg"
              onRatingChange={(rating) => {
                console.log(`User rated ${game?.name} ${rating} stars`);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default HoverCard;
